import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react'
import type { LcOperationMode } from '@/api/admin'
import { lcTestCompletionAdminApi, TEST_COMPLETION_ERROR_MESSAGES } from '@/api/lcTestCompletion'

interface Props {
  organizationId: number
  mode: LcOperationMode
  onChanged: (mode: LcOperationMode) => void
  onResetRequested: () => void
}

function errorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('response' in error)) return null
  const response = (error as { response?: { data?: { error?: { code?: string } } } }).response
  return response?.data?.error?.code ?? null
}

export function operationModeErrorMessage(code: string | null): string {
  return code ? (TEST_COMPLETION_ERROR_MESSAGES[code] ?? code) : '운영 모드 변경에 실패했습니다.'
}

export function LcOperationModeField({ organizationId, mode, onChanged, onResetRequested }: Props) {
  const [blocker, setBlocker] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: (nextMode: LcOperationMode) => lcTestCompletionAdminApi.updateOperationMode(organizationId, nextMode),
    onSuccess: ({ mode: nextMode }) => {
      setBlocker(null)
      onChanged(nextMode)
    },
    onError: (error) => {
      const code = errorCode(error)
      setBlocker(operationModeErrorMessage(code))
    },
  })

  const changeMode = (nextMode: LcOperationMode) => {
    if (nextMode === mode) return
    if (
      nextMode === 'TEST'
      && !window.confirm('TEST 센터에서는 교사가 원생 학습 기록을 자동 생성할 수 있습니다. 이 센터를 TEST로 전환할까요?')
    ) return
    mutation.mutate(nextMode)
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" aria-label="LC 운영 모드">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-cyan-700" />
            <h3 className="font-semibold text-slate-900">LC 운영 모드</h3>
          </div>
          <p className="mt-1 text-sm text-slate-500">자동완료는 TEST 센터에서만 활성화됩니다.</p>
        </div>
        <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1">
          {(['PRODUCTION', 'TEST'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => changeMode(value)}
              disabled={mutation.isPending}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                mode === value
                  ? value === 'TEST' ? 'bg-amber-100 text-amber-800 shadow-sm' : 'bg-emerald-100 text-emerald-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {mutation.isPending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : value}
            </button>
          ))}
        </div>
      </div>
      {blocker && (
        <div className="mt-4 flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{blocker}</span>
          </div>
          {mode === 'TEST' && (
            <button type="button" onClick={onResetRequested} className="shrink-0 text-sm font-semibold text-amber-900 underline">
              센터 초기화
            </button>
          )}
        </div>
      )}
    </section>
  )
}
